import * as React from 'react'

import { aggregateRealTimeStatus } from './realTimeStatus'
import type { RealTimeStatus } from './realTimeStatus'

import type { JSX, ReactNode } from 'react'

type RealTimeContextValue = {
  /** The worst status of every open subscription. `idle` when none is open. */
  status: RealTimeStatus
  /**
   * Reports the status of one subscription. `null` drops the subscription,
   * which `useBufferRealTimeSub` does on unmount and while it is closed.
   */
  reportStatus: (subscriptionId: string, status: RealTimeStatus | null) => void
}

const RealTimeContext = React.createContext<RealTimeContextValue | null>(null)

/**
 * The seam between a view and the real-time transport.
 *
 * This provider and `useBufferRealTimeSub` are the whole real-time API a
 * component uses. No component reads the transport. A later move from
 * Server-Sent Events to WebSocket changes this directory and the Apollo link
 * chain, and no component.
 *
 * The provider collects the status of every open subscription and reports the
 * worst one. It opens no request on its own: a stream starts when a view
 * subscribes, and ends when that view unmounts.
 */
export const RealTimeProvider = ({
  children,
}: {
  children: ReactNode
}): JSX.Element => {
  const statusBySubscription = React.useRef(new Map<string, RealTimeStatus>())
  const [status, setStatus] = React.useState<RealTimeStatus>('idle')

  const reportStatus = React.useCallback(
    (subscriptionId: string, next: RealTimeStatus | null): void => {
      if (next === null) {
        statusBySubscription.current.delete(subscriptionId)
      } else {
        statusBySubscription.current.set(subscriptionId, next)
      }
      setStatus(
        aggregateRealTimeStatus([...statusBySubscription.current.values()]),
      )
    },
    [],
  )

  const value = React.useMemo<RealTimeContextValue>(
    () => ({ status, reportStatus }),
    [status, reportStatus],
  )

  return (
    <RealTimeContext.Provider value={value}>
      {children}
    </RealTimeContext.Provider>
  )
}

/**
 * Reads the real-time context. Throws when no {@link RealTimeProvider} is above
 * the caller, because a subscription outside the provider reports no status.
 */
export const useRealTimeContext = (): RealTimeContextValue => {
  const value = React.useContext(RealTimeContext)
  if (value === null) {
    throw new Error('useRealTimeContext needs a RealTimeProvider above it.')
  }
  return value
}

/** The connection status of every open subscription, for a view to render. */
export const useRealTimeStatus = (): RealTimeStatus =>
  useRealTimeContext().status
